import React from 'react';
import {
  MessageSquare,
  Mail,
  ArrowRightLeft,
  Download,
  Printer,
  UserPlus,
  X,
} from 'lucide-react';
import './CandidateActionToolbar.scss';

export type EvaluationValue = 'pending' | 'accepted' | 'doubtful' | 'rejected';

const EVAL_OPTIONS: { value: EvaluationValue; label: string }[] = [
  { value: 'pending', label: 'Naujas' },
  { value: 'accepted', label: 'Tinkamas' },
  { value: 'doubtful', label: 'Abejotinas' },
  { value: 'rejected', label: 'Netinkamas' },
];

interface CandidateActionToolbarProps {
  candidateName: string;
  hasComment: boolean;
  selectionsCount: number;
  /** Show the global candidate status dropdown (only when in a job pipeline). */
  showEvaluation?: boolean;
  evaluation?: EvaluationValue;
  onEvaluationChange?: (value: EvaluationValue) => void;
  onAddToSelection: () => void;
  onWriteComment: () => void;
  onForwardEmail: () => void;
  onMoveCandidate: () => void;
  onDownloadCv: () => void;
  onPrint: () => void;
  onClose: () => void;
}

interface ToolButton {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  badge?: number;
  active?: boolean;
}

/**
 * Sticky horizontal toolbar pinned above the CV body on the employer's
 * single-candidate detail page. Hosts the six globally relevant actions
 * (add to atrankas, comment, forward by email, move CV, download, print)
 * plus a close button on the far right.
 *
 * Why a tool bar and not the old top-right cluster? The actions multiplied
 * past what the corner could comfortably fit, and grouping them in one
 * sticky surface keeps them reachable while the user scrolls through long
 * CVs.
 */
const CandidateActionToolbar: React.FC<CandidateActionToolbarProps> = ({
  candidateName,
  hasComment,
  selectionsCount,
  showEvaluation = false,
  evaluation = 'pending',
  onEvaluationChange,
  onAddToSelection,
  onWriteComment,
  onForwardEmail,
  onMoveCandidate,
  onDownloadCv,
  onPrint,
  onClose,
}) => {
  const buttons: ToolButton[] = [
    {
      key: 'add',
      label: 'Įkelti į atrankas',
      icon: <UserPlus size={18} />,
      onClick: onAddToSelection,
      badge: selectionsCount > 0 ? selectionsCount : undefined,
    },
    {
      key: 'comment',
      label: 'Rašyti komentarą',
      icon: <MessageSquare size={18} />,
      onClick: onWriteComment,
      active: hasComment,
    },
    {
      key: 'email',
      label: 'Siųsti el. paštu',
      icon: <Mail size={18} />,
      onClick: onForwardEmail,
    },
    {
      key: 'move',
      label: 'Perkelti CV',
      icon: <ArrowRightLeft size={18} />,
      onClick: onMoveCandidate,
    },
    {
      key: 'download',
      label: 'Atsisiųsti CV',
      icon: <Download size={18} />,
      onClick: onDownloadCv,
    },
    {
      key: 'print',
      label: 'Spausdinti',
      icon: <Printer size={18} />,
      onClick: onPrint,
    },
  ];

  return (
    <div className="candidate-action-toolbar" role="toolbar" aria-label={`${candidateName} veiksmai`}>
      {showEvaluation && onEvaluationChange && (
        <div className={`candidate-action-toolbar__status candidate-action-toolbar__status--${evaluation}`}>
          <span className="status-dot" aria-hidden="true" />
          <label htmlFor="candidate-eval-select">Būsena:</label>
          <select
            id="candidate-eval-select"
            value={evaluation}
            onChange={(e) => onEvaluationChange(e.target.value as EvaluationValue)}
          >
            {EVAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="candidate-action-toolbar__group">
        {buttons.map((b) => (
          <button
            key={b.key}
            type="button"
            className={`candidate-action-toolbar__btn${b.active ? ' is-active' : ''}`}
            onClick={b.onClick}
            title={b.label}
          >
            <span className="candidate-action-toolbar__icon">
              {b.icon}
              {b.badge !== undefined && (
                <span className="candidate-action-toolbar__badge">{b.badge}</span>
              )}
            </span>
            <span className="candidate-action-toolbar__label">{b.label}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="candidate-action-toolbar__close"
        onClick={onClose}
        title="Uždaryti"
        aria-label="Uždaryti"
      >
        <X size={20} />
      </button>
    </div>
  );
};

export default CandidateActionToolbar;
